export const SIGNAL_SCORE_DEFAULTS = {
  MOMENTUM_SCORE: { weight: 20, threshold: 10, metric: "momentumCount", metricLabel: "momentum_count" },
  TREND_SCORE: { weight: 10, threshold: 10, metric: "trendCount", metricLabel: "trend_count" },
  LIQUIDITY_SCORE: { weight: 15, threshold: 10, metric: "liquidityCount", metricLabel: "liquidity_count" },
  VOLATILITY_SCORE: { weight: 10, threshold: 10, metric: "volatilityCount", metricLabel: "volatility_count" },
  TSI: { weight: 20, threshold: 10, metric: "tsiCount", metricLabel: "tsi_count" },
  RELATIVE_STRENGTH: { weight: 20, threshold: 10, metric: "relativeStrengthCount", metricLabel: "relative_strength_count" },
  RVOL: { weight: 15, threshold: 10, metric: "rvolCount", metricLabel: "rvol_count" },
  RECENT_DRAWDOWN: { weight: 10, threshold: 10, metric: "drawdownCount", metricLabel: "drawdown_count" },
  VOLUME_TREND: { weight: 10, threshold: 10, metric: "volumeTrendCount", metricLabel: "volume_trend_count" },
};

const SIGNAL_RULE_ALIASES = {
  relativestrength: "RELATIVE_STRENGTH",
  momentum: "MOMENTUM_SCORE",
  momentumscore: "MOMENTUM_SCORE",
  trend: "TREND_SCORE",
  trendscore: "TREND_SCORE",
  liquidity: "LIQUIDITY_SCORE",
  liquidityscore: "LIQUIDITY_SCORE",
  volatility: "VOLATILITY_SCORE",
  volatilityscore: "VOLATILITY_SCORE",
  tsi: "TSI",
  rvol: "RVOL",
  recentdrawdown: "RECENT_DRAWDOWN",
  volumetrend: "VOLUME_TREND",
};

function toRuleKey(value) {
  return String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

function toAliasKey(value) {
  return String(value ?? "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

export function resolveSignalRuleName(group = {}) {
  const candidates = [group.ruleName, group.id, group.label].filter(Boolean);
  const directRule = candidates.map(toRuleKey).find((ruleName) => SIGNAL_SCORE_DEFAULTS[ruleName]);
  if (directRule) return directRule;

  const aliasRule = candidates
    .map(toAliasKey)
    .map((key) => SIGNAL_RULE_ALIASES[key])
    .find(Boolean);
  if (aliasRule) return aliasRule;

  return toRuleKey(group.ruleName ?? group.id ?? group.label ?? "SIGNAL");
}

export function getSignalScoreDefaults(group = {}) {
  const ruleName = typeof group === "string" ? group : resolveSignalRuleName(group);
  return SIGNAL_SCORE_DEFAULTS[ruleName] ?? {
    weight: 10,
    threshold: 0,
    metric: null,
    metricLabel: "selected_signal_match",
  };
}

export function createDefaultScoringConfig() {
  return Object.entries(SIGNAL_SCORE_DEFAULTS).reduce((config, [ruleName, rule]) => {
    config[ruleName] = {
      weight: rule.weight,
      threshold: rule.threshold,
    };
    return config;
  }, {});
}

function toNumber(value, fallback) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function normalizeSignalValue(value) {
  return String(value ?? "").trim().replace(/_/g, " ").toUpperCase();
}

function getStockSignalValue(stock, group, resolvedRuleName) {
  const signalKeys = [group.id, group.ruleName, group.label, resolvedRuleName].filter(Boolean);
  return signalKeys.map((key) => stock.signals?.[key]).find((value) => value !== undefined && value !== null);
}

function getSelectedSignalMatchCount(stock, group, resolvedRuleName, selectedValues = []) {
  const stockValue = getStockSignalValue(stock, group, resolvedRuleName);
  if (stockValue === undefined || stockValue === null) return 0;

  const selectedSet = new Set(selectedValues.map(normalizeSignalValue));
  const stockValues = Array.isArray(stockValue) ? stockValue : [stockValue];
  return stockValues.some((value) => selectedSet.has(normalizeSignalValue(value))) ? 1 : 0;
}

export function rescoreStocks(stocks = [], signals = [], selectedFilters = {}, scoringConfig = {}, strategy = {}) {
  const selectedGroups = signals.map((group) => ({
    ...group,
    resolvedRuleName: resolveSignalRuleName(group),
  })).filter((group) => {
    const selectedValues = selectedFilters[group.id] ?? [];
    return selectedValues.length;
  });

  if (!selectedGroups.length) {
    return stocks;
  }

  const mode = String(strategy?.mode ?? "BALANCED").toUpperCase();
  const minScore = toNumber(strategy?.minScore, 60);
  const minimumLooseMatches = Math.min(2, selectedGroups.length);

  return stocks
    .map((stock) => {
      let score = 0;
      let matchedSignalCount = 0;

      selectedGroups.forEach((group) => {
        const defaults = getSignalScoreDefaults(group);
        const config = scoringConfig[group.resolvedRuleName] ?? {};
        const weight = toNumber(config.weight, defaults.weight);
        const threshold = toNumber(config.threshold, defaults.threshold);
        const selectedValues = selectedFilters[group.id] ?? [];
        const count = defaults.metric
          ? toNumber(stock[defaults.metric], 0)
          : getSelectedSignalMatchCount(stock, group, group.resolvedRuleName, selectedValues);

        if (count > threshold) {
          score += weight;
          matchedSignalCount += 1;
        }
      });

      return {
        ...stock,
        score,
        matchedSignalCount,
        simulatedScore: score,
      };
    })
    .filter((stock) => {
      if (mode === "STRICT") return stock.matchedSignalCount === selectedGroups.length;
      if (mode === "LOOSE") return stock.matchedSignalCount >= minimumLooseMatches;
      return stock.score >= minScore;
    })
    .sort((a, b) => b.score - a.score || a.symbol.localeCompare(b.symbol));
}
